// JobMediaSection.js
// Photos & signatures for a job, shown in the Edit Job dialog. Photo uploads
// go to the existing job documents endpoint, so they appear alongside other
// job documents everywhere in the portal. Signatures go through the terms +
// signature flow (SignatureDialog): the customer signs on this device after
// reading the service terms, or is sent a link to sign on their own phone.
// Deliberately enabled for driver-only users (including temporary providers):
// they cannot edit job fields, but they can and should attach photos and
// capture customer signatures.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Paper, Typography, Button, Grid, Link, Alert, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Chip
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DrawIcon from '@mui/icons-material/Gesture';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import SignatureDialog from './SignatureDialog';

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const JobMediaSection = ({ jobId, job }) => {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { severity, message }
  const [signature, setSignature] = useState(job?.signature || { status: 'none' });
  const [signatureOpen, setSignatureOpen] = useState(false);

  const apiBase = `${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`;

  const fetchDocuments = useCallback(async () => {
    if (!jobId) return;
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${apiBase}/documents`, { headers: getAuthHeader() });
      setDocuments(res.data.documents || res.data || []);
    } catch (err) {
      console.error('Error fetching job documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [jobId, apiBase]);

  // Current signature state (the /terms endpoint returns it alongside the terms)
  const fetchSignature = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${apiBase}/terms`, { headers: getAuthHeader() });
      if (res.data?.signature) setSignature(res.data.signature);
    } catch (err) {
      // Offer recipients get 403 until they accept; keep whatever we have
    }
  }, [jobId, apiBase]);

  useEffect(() => { fetchDocuments(); fetchSignature(); }, [fetchDocuments, fetchSignature]);

  const uploadFiles = async (files, successMessage) => {
    setUploading(true);
    setStatus(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('documents', f));
      await axios.post(`${apiBase}/upload`, formData, { headers: getAuthHeader() });
      setStatus({ severity: 'success', message: successMessage });
      fetchDocuments();
    } catch (err) {
      setStatus({ severity: 'error', message: err.response?.data?.message || 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    uploadFiles(files, `${files.length} photo${files.length === 1 ? '' : 's'} uploaded.`);
  };

  const handleSignatureChange = (next, action) => {
    if (next) setSignature(next);
    if (action === 'signed') {
      setStatus({ severity: 'success', message: 'Signature saved.' });
      fetchDocuments();
    } else if (action === 'sent') {
      setStatus({ severity: 'info', message: `Signing link sent to ${next?.sentTo || 'the customer'}.` });
    } else if (action === 'cancelled') {
      setStatus({ severity: 'info', message: 'Signature request cancelled.' });
    }
  };

  const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(name || '');

  // The document view route requires the auth header, so fetch as a blob and
  // open in a new tab rather than using a plain link
  const handleOpenDocument = async (doc) => {
    try {
      const res = await axios.get(`${apiBase}/documents/${encodeURIComponent(doc.filename)}/view`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setStatus({ severity: 'error', message: 'Could not open the file.' });
    }
  };

  if (!jobId) return null; // Only for existing jobs (edit mode)

  const signatureChip = signature?.status === 'signed' ? (
    <Chip
      size="small"
      color="success"
      label={`Signed by ${signature.signerName || 'customer'}${signature.signedAt ? ` on ${new Date(signature.signedAt).toLocaleString()}` : ''}`}
    />
  ) : signature?.status === 'pending' ? (
    <Chip size="small" color="warning" label={`Awaiting signature, sent to ${signature.sentTo || 'customer'}`} />
  ) : null;

  const signatureButtonLabel = signature?.status === 'signed'
    ? 'Re-capture Signature'
    : signature?.status === 'pending' ? 'Signature Request…' : 'Capture Signature';

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Photos & Signatures</Typography>

      {status && (
        <Alert severity={status.severity} sx={{ mb: 1 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      <Grid container spacing={1} sx={{ mb: 1 }} alignItems="center">
        <Grid item>
          <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} disabled={uploading}>
            Upload Photos
            <input type="file" hidden accept="image/*" multiple onChange={handlePhotoSelect} />
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<DrawIcon />}
            onClick={() => setSignatureOpen(true)}
            disabled={uploading}
          >
            {signatureButtonLabel}
          </Button>
        </Grid>
        {signatureChip && <Grid item>{signatureChip}</Grid>}
        {uploading && <Grid item><CircularProgress size={24} sx={{ mt: 1 }} /></Grid>}
      </Grid>

      <SignatureDialog
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        jobId={jobId}
        job={job}
        signature={signature}
        onSignatureChange={handleSignatureChange}
      />

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Attached files {loadingDocs && <CircularProgress size={12} sx={{ ml: 1 }} />}
      </Typography>
      {documents.length === 0 && !loadingDocs ? (
        <Typography variant="body2" color="text.secondary">No photos or signatures attached yet.</Typography>
      ) : (
        <List dense>
          {documents.map((doc, idx) => (
            <ListItem key={doc.filename || idx} disablePadding sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isImage(doc.filename) ? <ImageIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Link component="button" type="button" onClick={() => handleOpenDocument(doc)} underline="hover" sx={{ textAlign: 'left' }}>
                    {doc.filename}
                  </Link>
                }
                secondary={doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : null}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default JobMediaSection;
